import { Component, ElementRef, OnDestroy, OnInit, AfterViewInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { Subscription } from 'rxjs';
import { LanguageService, LanguageCode } from '../../../services/i18n/language';

const SLIDE_COUNT = 6;
const AUTOPLAY_MS = 6000;
const SWIPE_PX = 40;
/** На такой ширине клик по слайду открывает полноэкранный горизонтальный просмотр. */
const MOBILE_QUERY = '(max-width: 768px)';
const FS_HISTORY_KEY = 'presentationFullscreen';

/** Языки, для которых есть папка assets/presentations/<код>. */
const PRESENTATION_LANGUAGES: LanguageCode[] = ['ru', 'kk', 'en'];
/** Какую версию слайдов показывать для языков без своей папки (по умолчанию — русскую). */
const PRESENTATION_FALLBACK: Partial<Record<LanguageCode, LanguageCode>> = { zh: 'en' };

interface Slide {
  src: string;
  caption: string;
  label: string;
}

@Component({
  selector: 'app-presentation-carousel',
  standalone: true,
  imports: [CommonModule, A11yModule],
  templateUrl: './presentation-carousel.component.html',
  styleUrl: './presentation-carousel.component.scss'
})
export class PresentationCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
  index = 0;
  slides: Slide[] = [];
  /** Открыт ли полноэкранный просмотр (мобильная версия). */
  fullscreen = false;

  eyebrowText = 'Платформа в работе';
  titleText = 'Посмотрите, как это работает';
  hintText = 'Кликните на слайды или выберите раздел';
  prevText = 'Предыдущий слайд';
  nextText = 'Следующий слайд';
  slideOfText = 'Слайд';
  tabsLabelText = 'Разделы презентации';
  closeText = 'Закрыть';

  private folder = 'ru';
  private pausedByUser = false;
  private inView = false;
  private tabVisible = true;
  private reducedMotion = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private touchStartX: number | null = null;
  private fsTouchStart: { x: number; y: number } | null = null;
  private observer?: IntersectionObserver;
  private subscriptions = new Subscription();

  private readonly captionDefaults = [
    'Международная поставка в одном окне: от расчёта до доставки',
    'Узнайте стоимость поставки заранее: предварительный расчёт таможенных платежей по инвойсу',
    'Сравнивайте ставки перевозчиков: стоимость и сроки доставки',
    'Узнайте, какие сертификаты нужны для груза, и закажите оформление на платформе',
    'Берём на себя всю боль с таможней: декларация и таможенный контроль до выпуска товаров',
    'Вся поставка, один результат: расходы, документы и история сделки в одном месте'
  ];
  private readonly labelDefaults = ['Обзор', 'Расчёт', 'Перевозка', 'Сертификаты', 'Таможня', 'Итог'];

  constructor(
    private languageService: LanguageService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  /** Автоплей включён, если пользователь не отключил анимации в системе. */
  get autoplayAvailable(): boolean {
    return !this.reducedMotion;
  }

  get autoplayRunning(): boolean {
    return this.autoplayAvailable && this.inView && this.tabVisible && !this.pausedByUser && !this.fullscreen;
  }

  ngOnInit(): void {
    this.reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );
    // Сервис языков отдаёт переводы раньше, чем обновляет текущий язык, — поэтому папку слайдов
    // пересчитываем ещё и по самой смене языка.
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(() => {
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.inView = true;
      this.schedule();
      return;
    }
    this.observer = new IntersectionObserver(
      entries => {
        this.zone.run(() => {
          this.inView = entries.some(e => e.isIntersecting);
          this.schedule();
          this.cdr.markForCheck();
        });
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.observer?.disconnect();
    this.subscriptions.unsubscribe();
    if (this.fullscreen) this.finishClose();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  next(): void {
    this.goTo((this.index + 1) % this.slides.length);
  }

  prev(): void {
    this.goTo((this.index - 1 + this.slides.length) % this.slides.length);
  }

  goTo(i: number): void {
    this.index = i;
    this.schedule();
  }

  pad(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }

  pause(): void {
    this.pausedByUser = true;
    this.clearTimer();
  }

  resume(): void {
    this.pausedByUser = false;
    this.schedule();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  /**
   * Клик по слайду. На мобильном — открывает слайд на весь экран горизонтально,
   * на десктопе: левая треть экрана — назад, остальное — вперёд.
   */
  onSlideClick(event: MouseEvent): void {
    if (window.matchMedia?.(MOBILE_QUERY).matches) {
      this.openFullscreen();
      return;
    }
    const rect = (event.currentTarget as HTMLElement).parentElement!.getBoundingClientRect();
    if (event.clientX - rect.left < rect.width / 3) this.prev(); else this.next();
  }

  openFullscreen(): void {
    if (this.fullscreen) return;
    this.fullscreen = true;
    this.clearTimer();
    document.body.style.overflow = 'hidden';
    // Запись в истории, чтобы системная кнопка «Назад» закрывала просмотр, а не уходила со страницы
    history.pushState({ [FS_HISTORY_KEY]: true }, '');
    window.addEventListener('popstate', this.onPopState);
    this.cdr.detectChanges(); // оверлей должен появиться в DOM синхронно, пока действует жест пользователя
    // Android Chrome: настоящий полноэкранный режим + альбомная ориентация. Где не поддерживается
    // (iOS Safari), слайд поворачивается через CSS, пока экран в портретной ориентации.
    const el = this.host.nativeElement.querySelector('.fs') as HTMLElement | null;
    el?.requestFullscreen?.()
      .then(() => (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.('landscape'))
      .catch(() => {});
  }

  closeFullscreen(): void {
    if (!this.fullscreen) return;
    if (history.state?.[FS_HISTORY_KEY]) {
      history.back(); // закрытие произойдёт в onPopState
    } else {
      this.finishClose();
    }
  }

  onFsKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeFullscreen();
    } else {
      this.onKeydown(event);
    }
  }

  onFsTouchStart(event: TouchEvent): void {
    const t = event.changedTouches[0];
    this.fsTouchStart = t ? { x: t.clientX, y: t.clientY } : null;
  }

  onFsTouchEnd(event: TouchEvent): void {
    const start = this.fsTouchStart;
    const t = event.changedTouches[0];
    this.fsTouchStart = null;
    if (!start || !t) return;
    // Когда слайд повёрнут на 90°, «горизонталь» слайда — это вертикаль экрана
    const rotated = window.matchMedia('(orientation: portrait)').matches;
    const d = rotated ? t.clientY - start.y : t.clientX - start.x;
    if (Math.abs(d) < SWIPE_PX) return;
    event.preventDefault(); // не даём свайпу превратиться в клик
    if (d < 0) this.next(); else this.prev();
  }

  private onPopState = (): void => {
    this.zone.run(() => {
      this.finishClose();
      this.cdr.markForCheck();
    });
  };

  private finishClose(): void {
    this.fullscreen = false;
    window.removeEventListener('popstate', this.onPopState);
    document.body.style.overflow = '';
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
    this.schedule();
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0]?.clientX ?? null;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.touchStartX === null) return;
    const dx = (event.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX;
    this.touchStartX = null;
    if (Math.abs(dx) < SWIPE_PX) return;
    if (dx < 0) this.next(); else this.prev();
  }

  readonly autoplayMs = AUTOPLAY_MS;

  private onVisibility = (): void => {
    this.zone.run(() => {
      this.tabVisible = document.visibilityState === 'visible';
      this.schedule();
      this.cdr.markForCheck();
    });
  };

  private schedule(): void {
    this.clearTimer();
    if (!this.autoplayRunning) return;
    this.timer = setTimeout(() => {
      this.zone.run(() => {
        this.next();
        this.cdr.markForCheck();
      });
    }, AUTOPLAY_MS);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    const lang = this.languageService.getCurrentLanguageValue();
    this.folder = PRESENTATION_LANGUAGES.includes(lang) ? lang : PRESENTATION_FALLBACK[lang] ?? 'ru';

    this.eyebrowText = this.t('presentation.eyebrow', this.eyebrowText);
    this.titleText = this.t('presentation.title', this.titleText);
    this.hintText = this.t('presentation.hint', this.hintText);
    this.prevText = this.t('presentation.prev', this.prevText);
    this.nextText = this.t('presentation.next', this.nextText);
    this.slideOfText = this.t('presentation.slide', this.slideOfText);
    this.tabsLabelText = this.t('presentation.tabsLabel', this.tabsLabelText);
    this.closeText = this.t('presentation.close', this.closeText);

    this.slides = Array.from({ length: SLIDE_COUNT }, (_, i) => ({
      src: `/assets/presentations/${this.folder}/${i + 1}.jpeg`,
      caption: this.t(`presentation.slide${i + 1}Caption`, this.captionDefaults[i]),
      label: this.t(`presentation.slide${i + 1}Label`, this.labelDefaults[i])
    }));
  }
}
